/**
 * DualPane — a top-level tab that stacks TWO other inner tabs (query / data)
 * for side-by-side comparison. The slots don't render the tabs themselves; each
 * slot is a portal *target* (a plain div) into which the owning Browser relocates
 * the live tab's DOM (Approach B — the tab stays mounted/owned by its Browser, so
 * its MySQL session + result grids survive). This component only lays out the two
 * slots, the divider, and the per-slot / pane headers.
 *
 * Props:
 *   pane              { id, title, orientation: 'tb'|'lr', ratio, slot1, slot2 }
 *   slotInfo(slot)    → { title, kind, resultsOnly } | null   (for slot headers)
 *   registerSlotEl(slot, el)   — hand the slot body div up to Shell's registry
 *   onRename(title) / onOrientation(o) / onRatio(r) / onSyncScroll(bool)
 *   onEjectSlot(slot) / onToggleResultsOnly(slot)
 */
import { createSignal, createMemo, Show, onMount, onCleanup } from 'solid-js';

export default function DualPane(props) {
  const orientation = () => props.pane.orientation || 'tb';
  const isTB = () => orientation() === 'tb';

  // ── Synced scrolling between the two slots' result/data grids. ──
  // Capture-phase listeners on each slot body catch scrolls from whichever
  // `.grid-scroller` is inside; we mirror scrollTop/Left to the other slot's
  // grid, guarded by `syncing` so the echoed scroll event doesn't loop back.
  const slotBodyEls = {};
  let syncing = false;
  // Scrollers we sync between slots: data/query result grids and the table
  // editor's body (columns / triggers lists). We match the source scroller's
  // selector and mirror to the SAME selector in the other slot, so same-type
  // panes scroll together (and unrelated cross-type pairs simply don't).
  const SYNC_SCROLLERS = ['.grid-scroller', '.script-editor-body'];
  const mirrorScroll = (fromSlot, src) => {
    if (!props.pane.syncScroll || syncing || !src?.matches) return;
    const sel = SYNC_SCROLLERS.find((s) => src.matches(s));
    if (!sel) return;
    const dst = slotBodyEls[fromSlot === 'slot1' ? 'slot2' : 'slot1']?.querySelector(sel);
    if (!dst || dst === src) return;
    syncing = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => { syncing = false; });
  };
  onMount(() => {
    const offs = [];
    for (const slot of ['slot1', 'slot2']) {
      const el = slotBodyEls[slot];
      if (!el) continue;
      const h = (e) => mirrorScroll(slot, e.target);
      el.addEventListener('scroll', h, { capture: true, passive: true });
      offs.push(() => el.removeEventListener('scroll', h, { capture: true }));
    }
    onCleanup(() => offs.forEach((f) => f()));
  });

  // ── Inline divider drag → ratio (fraction for slot1). ──
  let bodyEl;
  const onDividerDown = (e) => {
    e.preventDefault();
    const rect = bodyEl.getBoundingClientRect();
    const move = (ev) => {
      const frac = isTB()
        ? (ev.clientY - rect.top) / rect.height
        : (ev.clientX - rect.left) / rect.width;
      props.onRatio?.(Math.min(0.85, Math.max(0.15, frac)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── Pane title rename (double-click), like query-tab rename. ──
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal('');
  const startRename = () => { setDraft(props.pane.title); setEditing(true); };
  const commitRename = () => {
    const t = draft().trim();
    if (t) props.onRename?.(t);
    setEditing(false);
  };

  const cycleOrientation = () => props.onOrientation?.(isTB() ? 'lr' : 'tb');

  const ratio = () => props.pane.ratio ?? 0.5;
  const slot1Style = () => `flex: 0 0 ${ratio() * 100}%`;

  const Slot = (sp) => {
    onCleanup(() => props.registerSlotEl?.(sp.slot, null));
    const info = createMemo(() => props.slotInfo?.(sp.slot)); // memoized: read many times in the header
    // Orientation-dependent label, computed INSIDE Slot (as an accessor) so the
    // Slot(...) call args read no signals — otherwise an orientation flip would
    // re-invoke Slot, recreating the slot DOM and dropping its scroll listeners.
    const label = () => sp.slot === 'slot1' ? (isTB() ? 'Top' : 'Left') : (isTB() ? 'Bottom' : 'Right');
    return (
      <div class="dual-slot">
        <div class="dual-slot-header">
          <Show
            when={info()}
            fallback={<span class="muted dual-slot-empty-label">{label()} — right-click a query/data tab → “Send to dual pane”</span>}
          >
            <span class="dual-slot-title" title={info().title}>
              <span class={`dual-slot-kind dual-slot-kind-${info().kind}`}>{info().kind}</span>
              {info().title}
            </span>
            <span class="dual-slot-actions">
              <Show when={info().kind === 'query'}>
                <button class="btn btn-ghost btn-sm"
                        title={info().resultsOnly ? 'Show full tab (editor + results)' : 'Show results only'}
                        onClick={() => props.onToggleResultsOnly?.(sp.slot)}>
                  {info().resultsOnly ? '⤢' : '▤'}
                </button>
              </Show>
              <button class="btn btn-ghost btn-sm" title="Return to its connection"
                      onClick={() => props.onEjectSlot?.(sp.slot)}>⤴ Eject</button>
            </span>
          </Show>
        </div>
        <div class="dual-slot-body" ref={(e) => { props.registerSlotEl?.(sp.slot, e); slotBodyEls[sp.slot] = e; }} />
      </div>
    );
  };

  return (
    <div class="dual-pane">
      <div class="dual-pane-header" onContextMenu={(e) => { e.preventDefault(); cycleOrientation(); }}>
        <Show when={editing()} fallback={
          <span class="dual-pane-title" onDblClick={startRename} title="Double-click to rename">{props.pane.title}</span>
        }>
          <input class="input dual-pane-title-input" autofocus value={draft()}
                 onInput={(e) => setDraft(e.target.value)}
                 onBlur={commitRename}
                 onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditing(false); }} />
        </Show>
        <div class="spacer" />
        <button class={`btn btn-sm ${props.pane.syncScroll ? '' : 'btn-ghost'}`}
                title={props.pane.syncScroll ? 'Synced scrolling is ON — click to unlink the two panes' : 'Sync scrolling between the two panes (aligns their result grids)'}
                onClick={() => props.onSyncScroll?.(!props.pane.syncScroll)}>
          🔗 Sync scroll
        </button>
        <button class="btn btn-ghost btn-sm" title="Switch layout (or right-click the header)" onClick={cycleOrientation}>
          {isTB() ? '⬍ Top/Bottom' : '⬌ Left/Right'}
        </button>
      </div>

      <div class={`dual-pane-body ${isTB() ? 'dual-tb' : 'dual-lr'}`} ref={bodyEl}>
        <div class="dual-slot-wrap" style={slot1Style()}>{Slot({ slot: 'slot1' })}</div>
        <div class={`dual-divider ${isTB() ? 'dual-divider-tb' : 'dual-divider-lr'}`} onPointerDown={onDividerDown} />
        <div class="dual-slot-wrap" style="flex:1 1 0;min-height:0;min-width:0;">{Slot({ slot: 'slot2' })}</div>
      </div>
    </div>
  );
}
